import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DownloadOutlined, NotesOutlined } from '@mui/icons-material';
import { useWorkspace } from '../../../utils/WorkspaceContext';
import useCurrentUser from '../../../utils/useCurrentUser';
import api from '../../../services/api';
import GoalTable from './GoalTable';
import NarrativeSections, { SectionBand } from './NarrativeSections';
import WqqiTable from './WqqiTable';
import { sheetTone } from './sheetStyles';

const STATUS_COLOR = {
  Draft: 'default',
  Submitted: 'info',
  Reviewed: 'warning',
  Finalized: 'success',
};

/** The next status the current user may move a report to, or null. */
const nextTransition = (report, permissions) => {
  if (!report) return null;
  switch (report.status) {
    case 'Draft':
      return { label: 'Submit', status: 'Submitted', allowed: true };
    case 'Submitted':
      return { label: 'Mark Reviewed', status: 'Reviewed', allowed: permissions?.is_supervisor };
    case 'Reviewed':
      return { label: 'Finalize', status: 'Finalized', allowed: permissions?.is_supervisor };
    default:
      return { label: 'Reopen', status: 'Draft', allowed: permissions?.is_supervisor };
  }
};

/**
 * The biweekly performance review, laid out like the team's spreadsheet:
 * a derived Project/Track/Goal table, the narrative bands, and the manually
 * filled Work Quality & Quantum table with employee and supervisor halves.
 *
 * The report is fetched (or created) for the chosen SprintCalendar window on
 * load, and every edit is a PATCH on blur — there is no explicit save.
 */
const BiWeeklyReport = () => {
  const theme = useTheme();
  const colors = sheetTone(theme);
  const { currentWorkspace } = useWorkspace();
  const { user: currentUser } = useCurrentUser();

  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const permissions = report?.permissions;
  const transition = useMemo(() => nextTransition(report, permissions), [report, permissions]);

  // The SprintCalendar windows are the biweekly periods; default to the one
  // covering today so the page opens on the current fortnight.
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    api
      .get('sprint-calendars/', { params: { workspace_id: currentWorkspace.id } })
      .then(({ data }) => {
        // This endpoint wraps its list in `payload`; tolerate a bare list too.
        const windows = Array.isArray(data) ? data : data?.payload || [];
        setPeriods(windows);
        const today = new Date().toISOString().slice(0, 10);
        const current = windows.find((p) => p.start_date <= today && p.end_date >= today);
        setPeriodId(String((current || windows[windows.length - 1])?.id || ''));
      })
      .catch(() => setPeriods([]));
  }, [currentWorkspace]);

  // Only a supervisor may open someone else's report, so the picker is only
  // rendered for them. The list already includes the current user, so it is the
  // single source of names — the selection just defaults to whoever is looking.
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    api
      .get('assignees/', { params: { workspace_id: currentWorkspace.id } })
      .then(({ data }) => setAssignees(Array.isArray(data) ? data : data?.payload || []))
      .catch(() => setAssignees([]));
  }, [currentWorkspace]);

  useEffect(() => {
    if (!employeeId && currentUser?.id) setEmployeeId(String(currentUser.id));
  }, [currentUser, employeeId]);

  const loadReport = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('biweekly-reports/ensure/', {
        workspace: currentWorkspace.id,
        ...(periodId ? { period: Number(periodId) } : {}),
        ...(employeeId ? { employee: Number(employeeId) } : {}),
      });
      setReport(data);
    } catch (err) {
      setReport(null);
      setError(
        err?.response?.status === 403
          ? 'You do not have access to this report.'
          : 'Could not load the biweekly report.',
      );
    }
    setLoading(false);
  }, [currentWorkspace, periodId, employeeId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  /** PATCH the report and adopt the server's response as the new truth. */
  const patchReport = async (payload) => {
    if (!report) return;
    try {
      const { data } = await api.patch(`biweekly-reports/${report.id}/`, payload);
      setReport(data);
    } catch (err) {
      setToast(err?.response?.data?.detail || 'Could not save that change.');
    }
  };

  /** PATCH one WQQI row, merging the result back into the report in place. */
  const patchRating = async (ratingId, payload) => {
    try {
      const { data } = await api.patch(`wqqi-ratings/${ratingId}/`, payload);
      setReport((prev) => prev && {
        ...prev,
        wqqi_ratings: prev.wqqi_ratings.map((row) => (row.id === ratingId ? data : row)),
      });
    } catch (err) {
      setToast(err?.response?.data?.detail || 'Could not save that rating.');
    }
  };

  const pullDailyNotes = async () => {
    try {
      const { data } = await api.post(`biweekly-reports/${report.id}/pull-daily-notes/`, {});
      setReport(data);
      setToast('Daily notes pulled in.');
    } catch (err) {
      setToast(err?.response?.data?.detail || 'Could not pull daily notes.');
    }
  };

  const runTransition = async () => {
    try {
      const { data } = await api.post(`biweekly-reports/${report.id}/transition/`, {
        status: transition.status,
      });
      setReport(data);
      setToast(`Report is now ${data.status}.`);
    } catch (err) {
      setToast(err?.response?.data?.detail || 'Could not change the status.');
    }
  };

  // The PDF is an authenticated binary download, so it goes through the api
  // instance (which attaches the token) rather than a bare link.
  const downloadPdf = async () => {
    try {
      const response = await api.get(`biweekly-reports/${report.id}/pdf/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `biweekly_${report.employee_name || 'report'}_${report.period_start}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setToast('Could not generate the PDF.');
    }
  };

  if (!currentWorkspace?.id) {
    return <Alert severity="info" sx={{ m: 2 }}>Pick a workspace to see its biweekly reports.</Alert>;
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Controls */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <TextField
          select
          size="small"
          label="Period"
          value={periodId}
          onChange={(e) => setPeriodId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {periods.length === 0 && <MenuItem value="">No sprint calendar yet</MenuItem>}
          {periods.map((period) => (
            <MenuItem key={period.id} value={String(period.id)}>
              {period.name} ({period.start_date} – {period.end_date})
            </MenuItem>
          ))}
        </TextField>

        {permissions?.is_supervisor && (
          <TextField
            select
            size="small"
            label="Employee"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {assignees.map((person) => (
              <MenuItem key={person.id} value={String(person.id)}>
                {person.full_name || person.username}
                {person.id === currentUser?.id ? ' (me)' : ''}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {report && (
          <>
            <Chip
              label={report.status}
              color={STATUS_COLOR[report.status] || 'default'}
              size="small"
            />
            {permissions?.can_edit_employee_fields && (
              <Tooltip title="Merge this period's daily notes into Priorities and Accomplishments">
                <Button size="small" startIcon={<NotesOutlined />} onClick={pullDailyNotes}>
                  Pull daily notes
                </Button>
              </Tooltip>
            )}
            {transition?.allowed && (
              <Button size="small" variant="outlined" onClick={runTransition}>
                {transition.label}
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadOutlined />}
              onClick={downloadPdf}
            >
              PDF
            </Button>
          </>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && report && (
        <>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              mb: 1,
              backgroundColor: colors.band,
              border: '1px solid',
              borderColor: colors.border,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Biweekly — {report.employee_name}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {report.period_start} – {report.period_end}
            {report.period_name ? ` · ${report.period_name}` : ''}
            {report.reviewed_by_name ? ` · Reviewed by ${report.reviewed_by_name}` : ''}
          </Typography>

          <GoalTable lines={report.lines} />

          <NarrativeSections
            report={report}
            permissions={permissions}
            onChange={patchReport}
          />

          <SectionBand
            title="Work Quality and Quantum"
            note="To be filled by supervisor"
            tone="supervisor"
          />
          <WqqiTable
            ratings={report.wqqi_ratings}
            permissions={permissions}
            onRatingChange={patchRating}
          />

        </>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
};

export default BiWeeklyReport;
